"use client"

import { LocaleLink as Link } from "@/components/locale-link"
import { BoothWordmark } from "@/components/booth-wordmark"
import { Briefcase, MapPin, Globe, BookOpen, Trophy, Mail } from "lucide-react"
import { useTranslations } from "next-intl"
import { useUserRole } from "@/providers/user-role-provider"
import { LocaleSwitcher } from "@/components/locale-switcher"

// Site-wide footer. Single source of truth — used on every public
// page (landing, blog, articles, pricing, rewards, privacy, terms,
// press, traction). Role-aware: the "For you" column flips between
// public / barber / owner so signed-in users see relevant entry
// points instead of "Sign Up" everywhere.

type FooterLink = { label: string; href: string; external?: boolean }

function ColumnLink({ link }: { link: FooterLink }) {
  if (link.external) {
    return (
      <a href={link.href} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors">
        {link.label}
      </a>
    )
  }
  return (
    <Link href={link.href} className="hover:text-foreground transition-colors">
      {link.label}
    </Link>
  )
}

export function PublicFooter() {
  const t = useTranslations("marketing.footer")
  const { userId, role, actualUserType } = useUserRole()
  const effective = actualUserType ?? role
  const isLoggedIn = !!userId

  // Column 1 — the audience doors, in the shape Paper's TOOLS column uses:
  // one product, three ways in. Signed in, this flips to the role-aware "For
  // You" set, because a barber who is already a barber doesn't need a link
  // telling them BOOTH is for barbers.
  //
  // Each door points at that audience's real surface today. There are no
  // /for-barbers landing pages yet; when they exist these three hrefs are the
  // only thing that changes.
  const product: FooterLink[] = [
    { label: t("links.forBarbers"), href: "/listings" },
    { label: t("links.forOwners"), href: "/pricing" },
    { label: t("links.forClients"), href: "/barbers" },
    { label: t("links.pricing"), href: "/pricing" },
    { label: t("links.rewards"), href: "/rewards" },
  ]

  const forYou: FooterLink[] = isLoggedIn
    ? effective === "owner"
      ? [
          { label: t("links.dashboard"), href: "/dashboard/owner" },
          { label: t("links.hiring"), href: "/hiring" },
          { label: t("links.network"), href: "/dashboard/owner/network" },
          { label: t("links.messages"), href: "/dashboard/owner/messages" },
          { label: t("links.account"), href: "/dashboard/account" },
        ]
      : [
          { label: t("links.dashboard"), href: "/dashboard/barber" },
          { label: t("links.saved"), href: "/dashboard/barber/saved" },
          { label: t("links.network"), href: "/dashboard/barber/network" },
          { label: t("links.messages"), href: "/dashboard/barber/messages" },
          { label: t("links.rewards"), href: "/dashboard/barber/rewards" },
        ]
    : product

  // "Discover" — always public-facing surfaces. Same for every user.
  // Community is intentionally omitted — it's an in-app-only feature now,
  // reached from the signed-in nav, not advertised on the public footer.
  const discover: FooterLink[] = [
    { label: t("links.browseShops"), href: "/listings" },
    { label: t("links.search"), href: "/search" },
    { label: t("links.browseBarbers"), href: "/barbers" },
    { label: t("links.jobBoard"), href: "/hiring" },
    // /booth-rental and its city pages are deliberately absent: they exist
    // for search engines (sitemap, llms.txt), not for people already on the
    // site, who belong on /search or /listings.
  ]

  // "Compare" — sits under Discover rather than in its own column, the way
  // Paper hangs COMPARE off RESOURCES. Someone weighing BOOTH against the
  // booking app they already pay for is discovering, not doing legal research,
  // and these pages answer the question the search engine was asked.
  const compare: FooterLink[] = [
    { label: t("links.vsSquire"), href: "/compare/booth-vs-squire" },
    { label: t("links.vsTheCut"), href: "/compare/booth-vs-thecut" },
    { label: t("links.vsBooksy"), href: "/compare/booth-vs-booksy" },
  ]

  // "Company" — about BOOTH the brand. Same for everyone.
  const company: FooterLink[] = [
    { label: t("links.articles"), href: "/blog" },
    { label: t("links.press"), href: "/press" },
    { label: t("links.traction"), href: "/traction" },
    { label: t("links.buildingInPublic"), href: "/changelog" },
    { label: t("links.bookDemo"), href: "https://cal.com/boothdemo/30min", external: true },
    // "Contact", not the raw address. These columns are 2 of 12 — around 200px
    // at desktop — and contact@boothapp.co has no space to break on, so it ran
    // straight past the column's underline and out of its grid cell, which is
    // what made the last row of Company look broken. It also read as raw data
    // sitting under four titled links. The mailto is unchanged.
    { label: t("links.contact"), href: "mailto:contact@boothapp.co", external: true },
  ]

  const legal: FooterLink[] = [
    { label: t("links.privacy"), href: "/privacy" },
    { label: t("links.terms"), href: "/terms" },
    { label: t("links.refunds"), href: "/refunds" },
  ]

  // Column header — architectural spec style: small mono number,
  // label, thin underline. Mirrors the hero shop-cycle label.
  // `index` prop kept on the call sites for now but no longer rendered —
  // dropped per design feedback (numbers read as IDs more than chrome).
  const SectionHeader = ({ label }: { index?: string; label: string }) => (
    <div className="mb-6 pb-3 border-b border-border">
      <h4 className="font-mono text-[10px] font-medium tracking-[0.16em] uppercase text-foreground">
        {label}
      </h4>
    </div>
  )

  return (
    <footer className="bg-background text-foreground pt-24 pb-32 md:pb-20 px-6 sm:px-10 lg:px-16 border-t border-border">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-y-12 gap-x-8 md:gap-x-12 lg:gap-x-20">
          {/* Brand block — wider, more breathing room */}
          {/* Tablet gives the brand its own row. At md the four link columns
              were 2 of 12 each — about 110px once the gap is taken out — so
              "Browse Shops", "Booth Rental by City" and every "BOOTH vs …"
              broke mid phrase. Full width here, then 3 of 12 per column
              below, which is 4 across on one row at roughly double the
              width. Desktop is unchanged: brand 4, columns 2. */}
          <div className="col-span-2 md:col-span-12 lg:col-span-4">
            <Link href="/" className="inline-block mb-6">
              <BoothWordmark className="h-5 w-auto" />
            </Link>
            <p className="text-muted-foreground text-sm leading-relaxed mb-6 max-w-xs">
              {t("tagline")}
            </p>
            {/* Sign Up / Log In used to be the first two rows of column one.
                Column one is the audience doors now, and a conversion pair
                filed under "Product" reads as two more product surfaces. They
                belong next to the wordmark, where they're the brand block's
                call to action rather than a list item. */}
            {!isLoggedIn && (
              <div className="flex items-center gap-3 mb-8">
                <Link
                  href="/auth/signup"
                  className="rounded-full bg-foreground text-background px-4 py-1.5 text-xs font-medium hover:opacity-90 transition-opacity"
                >
                  {t("links.signUp")}
                </Link>
                <Link
                  href="/auth/login"
                  className="text-xs text-muted-foreground hover:text-foreground transition-colors"
                >
                  {t("links.logIn")}
                </Link>
              </div>
            )}
            <div className="flex items-center gap-4 font-mono text-[10px] tracking-[0.16em] uppercase">
              <a
                href="https://www.instagram.com/booth__inc/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                Instagram
              </a>
              <span className="text-border">·</span>
              <a
                href="https://x.com/booth__app"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                X
              </a>
              <span className="text-border">·</span>
              <a
                href="https://tiktok.com/@booth.inc"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                TikTok
              </a>
              <span className="text-border">·</span>
              {/* The org profile is linked from /llms.txt and the Organization
                  schema's sameAs, and was in no visible <a> anywhere on the
                  site. Search engines discover pages by following links they
                  can see; sameAs associates an entity, it is not a crawl path.
                  This is the one link that appears on every page. */}
              <a
                href="https://github.com/booth-inc"
                target="_blank"
                rel="noopener noreferrer"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                GitHub
              </a>
            </div>
          </div>

          {/* Product / For You — role-aware */}
          <div className="md:col-span-3 lg:col-span-2">
            <SectionHeader index="01" label={isLoggedIn ? t("forYou") : t("product")} />
            <ul className="space-y-3 text-sm text-muted-foreground">
              {forYou.map(l => (
                <li key={l.href}><ColumnLink link={l} /></li>
              ))}
            </ul>
          </div>

          {/* Discover, with Compare hanging off the bottom of the same column */}
          <div className="md:col-span-3 lg:col-span-2">
            <SectionHeader index="02" label={t("discover")} />
            <ul className="space-y-3 text-sm text-muted-foreground">
              {discover.map(l => (
                <li key={l.href}><ColumnLink link={l} /></li>
              ))}
            </ul>
            <div className="mt-10">
              <SectionHeader label={t("compare")} />
              <ul className="space-y-3 text-sm text-muted-foreground">
                {compare.map(l => (
                  <li key={l.href}><ColumnLink link={l} /></li>
                ))}
              </ul>
            </div>
          </div>

          {/* Company */}
          <div className="md:col-span-3 lg:col-span-2">
            <SectionHeader index="03" label={t("company")} />
            <ul className="space-y-3 text-sm text-muted-foreground">
              {company.map(l => (
                <li key={l.href}><ColumnLink link={l} /></li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div className="md:col-span-3 lg:col-span-2">
            <SectionHeader index="04" label={t("legal")} />
            <ul className="space-y-3 text-sm text-muted-foreground">
              {legal.map(l => (
                <li key={l.href}><ColumnLink link={l} /></li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom row — copyright only. Mono + tracking matches hero
            shop-cycle label aesthetic. */}
        <div className="mt-20 pt-8 border-t border-border flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 font-mono text-[10px] tracking-[0.16em] uppercase text-muted-foreground">
          <p>&copy; {new Date().getFullYear()} BOOTH — {t("rights")}</p>
          <LocaleSwitcher className="normal-case tracking-normal text-muted-foreground" />
        </div>
      </div>
    </footer>
  )
}
